import { resumenDeMensaje, tarjetaDeMensaje } from "./mensaje";

/* =========================================================================
   LA CAMPANA DEL PANEL

   Los avisos del tendero —entró un pedido, le escribieron, se canceló algo—
   vivían solo en el teléfono. Sentado en el mostrador, el panel era el único
   sitio donde no se enteraba.

   DOS COSAS QUE ESTE ARCHIVO EXISTE PARA EVITAR:

   1. QUE SE VEA JSON. Por el chat viajan TARJETAS: el pedido que el tendero le
      arma a un cliente es un JSON, y el aviso copiaba ese contenido tal cual.
      En la campana aparecía `{"type":"preorder","preorder_id":17925…`. El
      servidor ya no lo manda así, pero LOS QUE YA ESTÁN GUARDADOS siguen ahí
      —son filas en la base de cada persona— y el arreglo del servidor solo
      cubre los nuevos. Por eso se limpia también al pintar.

   2. QUE UN AVISO NO LLEVE A NINGUNA PARTE. Un aviso que dice «entró un
      pedido» y no se puede tocar obliga a buscarlo a mano en el tablero.
      `rutaDelAviso` decide a dónde lleva cada uno, a partir de lo que el
      servidor guardó en `datos`.
   ======================================================================== */

/** Cómo se llama cada tipo de aviso, y con qué icono se dibuja. */
export const TIPOS = {
  pedido_nuevo: { titulo: "Pedido nuevo", icono: "receipt", tone: "ok" },
  pedido_aceptado: { titulo: "Pedido aceptado", icono: "check", tone: "info" },
  pedido_en_camino: { titulo: "Pedido en camino", icono: "bike", tone: "info" },
  pedido_entregado: { titulo: "Pedido entregado", icono: "check", tone: "ok" },
  pedido_cancelado: { titulo: "Pedido cancelado", icono: "close", tone: "bad" },
  entrega_asignada: { titulo: "Entrega asignada", icono: "bike", tone: "info" },
  mensaje_nuevo: { titulo: "Mensaje nuevo", icono: "chat", tone: "info" },
  promocion: { titulo: "Promoción", icono: "megaphone", tone: "brand" },
};

const GENERICO = { titulo: "Aviso", icono: "bell", tone: "neutral" };

export const tipoDeAviso = (aviso) => TIPOS[aviso?.tipo] ?? GENERICO;

/**
 * El texto que se lee en la campana.
 *
 * Se apoya en `resumenDeMensaje`, que es el mismo que usa la lista del chat:
 * una tarjeta de pedido se cuenta en una frase y una que esta versión no sabe
 * pintar dice qué es en vez de volcar sus datos.
 */
export function textoDelAviso(aviso) {
  const mensaje = String(aviso?.message ?? "").trim();

  if (mensaje === "") return "Te enviaron un aviso.";

  /* Solo se interpreta lo que parece una tarjeta: así un mensaje normal no
     pasa por JSON.parse en cada render. */
  return tarjetaDeMensaje(mensaje) ? resumenDeMensaje(mensaje) : mensaje;
}

/**
 * A dónde lleva un aviso cuando se toca.
 *
 * `null` significa que no lleva a ninguna parte, y entonces la campana NO lo
 * pinta como pulsable: un aviso que parece un enlace y no hace nada se siente
 * roto.
 *
 * Los pedidos van al tablero y no a una pantalla de detalle propia porque es
 * ahí donde el tendero los trabaja; el `#` del pedido se pasa en la dirección
 * para que el tablero lo abra.
 */
export function rutaDelAviso(aviso) {
  const pedido = aviso?.datos?.order_id;

  switch (aviso?.tipo) {
    case "pedido_nuevo":
    case "pedido_aceptado":
    case "pedido_en_camino":
    case "pedido_entregado":
    case "pedido_cancelado":
    case "entrega_asignada":
      return pedido ? `/?pedido=${pedido}` : "/";

    case "mensaje_nuevo":
      return "/chat";

    case "promocion":
      return "/promociones";

    default:
      return null;
  }
}

/** Cuántos quedan sin leer, para la burbuja. */
export function sinLeer(avisos) {
  return (avisos ?? []).filter((a) => !a?.read).length;
}

/**
 * La burbuja de la campana.
 *
 * Por encima de 9 se escribe «9+»: tres dígitos no caben en un círculo de esa
 * medida, y la diferencia entre doce y catorce avisos sin leer no cambia nada
 * de lo que el tendero va a hacer.
 */
export function burbuja(cuantos) {
  const n = Number(cuantos) || 0;

  if (n <= 0) return null;

  return n > 9 ? "9+" : String(n);
}
